import { Check, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { Badge } from "@/components/ui/Badge";
import { BentoCard } from "@/components/ui/BentoCard";
import {
  SUBSCRIPTION_PLAN_CARDS,
  type SubscriptionPlanCard,
} from "@domain/billing/plan-cards";
import {
  PLAN_DISPLAY_LABEL,
  PLAN_STATUS_DISPLAY_LABEL,
  type PlanStatus,
} from "@domain/billing/plan-sync";
import type { Plan } from "@domain/claw-config";
import type { PendingBillingAction } from "@/hooks/useBilling";
import { cn } from "@/lib/utils";

interface SubscriptionSectionProps {
  plan: Plan;
  /** null while getBillingInfo is loading — renders a neutral placeholder, never a "trial" flash. */
  planStatus: PlanStatus | null;
  pendingAction: PendingBillingAction;
  billingError: string | null;
  onSubscribe: () => void;
}

function PlanCard({
  card,
  isCurrent,
  cta,
}: {
  card: SubscriptionPlanCard;
  isCurrent: boolean;
  cta?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border p-6",
        isCurrent
          ? "border-brand-coral/40 bg-brand-coral/5"
          : "border-black/10 dark:border-white/10",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-black dark:text-white">
          {card.title}
        </span>
        {isCurrent && (
          <span className="rounded-full border border-brand-coral/40 bg-brand-coral/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-coral">
            Current plan
          </span>
        )}
      </div>
      <div className="mt-1 text-sm font-medium text-black/50 dark:text-white/50">
        {card.priceLine}
      </div>
      <ul className="mt-4 flex-1 space-y-2">
        {card.benefits.map((benefit) => (
          <li
            key={benefit}
            className="flex items-start gap-2 text-sm font-medium text-black/70 dark:text-white/70"
          >
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-green" />
            {benefit}
          </li>
        ))}
      </ul>
      {cta && <div className="mt-6">{cta}</div>}
    </div>
  );
}

/** Pure presentational subscription card — all state and actions come in as props. */
export function SubscriptionSection({
  plan,
  planStatus,
  pendingAction,
  billingError,
  onSubscribe,
}: SubscriptionSectionProps) {
  const anyPending = pendingAction !== null;
  const statusDisplay = planStatus ? PLAN_STATUS_DISPLAY_LABEL[planStatus] : null;

  return (
    <BentoCard shadow className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <span className="text-sm font-medium text-black/60 dark:text-white/60">
          Current Plan
        </span>
        <div className="flex items-center gap-2">
          {statusDisplay?.label !== PLAN_DISPLAY_LABEL[plan] && (
            <span className="rounded-full border border-black/10 bg-black/5 px-3 py-1 text-xs font-bold uppercase tracking-wide text-black/60 dark:border-white/10 dark:bg-white/10 dark:text-white/60">
              {PLAN_DISPLAY_LABEL[plan]}
            </span>
          )}
          {statusDisplay ? (
            <Badge variant={statusDisplay.tone}>{statusDisplay.label}</Badge>
          ) : (
            <Badge variant="default">…</Badge>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {SUBSCRIPTION_PLAN_CARDS.map((card) => (
          <PlanCard
            key={card.plan}
            card={card}
            isCurrent={plan === card.plan}
            cta={
              card.plan === "standard" ? (
                <button
                  disabled={anyPending}
                  onClick={onSubscribe}
                  className="flex items-center gap-2 rounded-lg border border-brand-coral/30 bg-brand-coral/10 px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-brand-coral transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {pendingAction === "subscription" && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {plan === "standard" ? "Manage subscription" : "Subscribe — $19.99/mo"}
                </button>
              ) : undefined
            }
          />
        ))}
      </div>

      {billingError && (
        <p className="mt-4 text-sm font-medium text-brand-coral">{billingError}</p>
      )}
    </BentoCard>
  );
}
